import { Trans, useLingui } from "@lingui/react/macro";
import type { DrokContract } from "@rakazo/contracts";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Input,
  Spinner,
  Textarea,
  toast,
} from "@rakazo/ui-web";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@rakazo/ui-web/components/ui/table";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useQueryUserId } from "../../lib/query";
import { errorText } from "../../lib/user-error";
import { drokRpc } from "../rpc";

type UserRow = Awaited<ReturnType<typeof drokRpc.users.list>>["users"][number];
type _Contract = DrokContract;

type Action =
  | { kind: "grant"; user: UserRow }
  | { kind: "revoke"; user: UserRow }
  | { kind: "block"; user: UserRow }
  | { kind: "unblock"; user: UserRow };

function usersKey(userId: string | null, search: string) {
  return ["drok", `user:${userId ?? ""}`, "admin.users", search] as const;
}

function StatusBadges({ user }: { user: UserRow }) {
  return (
    <div className="flex flex-wrap gap-1">
      {user.isIt ? (
        <Badge>
          <Trans>IT</Trans>
        </Badge>
      ) : null}
      {user.blocked ? (
        <Badge variant="destructive">
          <Trans>Blocked</Trans>
        </Badge>
      ) : null}
      {user.hasPersonalSpace ? null : (
        <Badge variant="outline">
          <Trans>Not signed in yet</Trans>
        </Badge>
      )}
    </div>
  );
}

function ActionDialog({
  action,
  selfId,
  onClose,
}: {
  action: Action | null;
  selfId: string | null;
  onClose: () => void;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: async (a: Action) => {
      const userId = a.user.id;
      if (a.kind === "grant") return drokRpc.users.grantIt({ userId });
      if (a.kind === "revoke") return drokRpc.users.revokeIt({ userId });
      if (a.kind === "unblock") return drokRpc.users.unblock({ userId });
      return drokRpc.users.block({ userId, reason: reason.trim() || undefined });
    },
    onSuccess: async (_data, a) => {
      toast.success(
        a.kind === "grant"
          ? t`${a.user.email} is now IT`
          : a.kind === "revoke"
            ? t`${a.user.email} is no longer IT`
            : a.kind === "block"
              ? t`${a.user.email} is blocked`
              : t`${a.user.email} is unblocked`,
      );
      await queryClient.invalidateQueries({ queryKey: ["drok", `user:${selfId ?? ""}`] });
      setReason("");
      onClose();
    },
    onError: (e) => setError(errorText(e)),
  });
  const close = () => {
    if (mutation.isPending) return;
    setError(null);
    setReason("");
    onClose();
  };
  const email = action?.user.email ?? "";
  return (
    <AlertDialog open={action !== null} onOpenChange={(open) => (open ? undefined : close())}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {action?.kind === "grant" ? <Trans>Make {email} IT?</Trans> : null}
            {action?.kind === "revoke" ? <Trans>Remove IT access from {email}?</Trans> : null}
            {action?.kind === "block" ? <Trans>Block {email}?</Trans> : null}
            {action?.kind === "unblock" ? <Trans>Unblock {email}?</Trans> : null}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {action?.kind === "grant" ? (
              <Trans>IT can build agents, publish templates and manage every user.</Trans>
            ) : null}
            {action?.kind === "revoke" ? (
              <Trans>They become an employee and lose access to these pages.</Trans>
            ) : null}
            {action?.kind === "block" ? (
              <Trans>
                They are signed out everywhere, their devices are revoked and their running work
                stops.
              </Trans>
            ) : null}
            {action?.kind === "unblock" ? <Trans>They can sign in again.</Trans> : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {action?.kind === "block" ? (
          <Textarea
            aria-label={t`Reason (optional)`}
            placeholder={t`Reason (optional)`}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={mutation.isPending} onClick={close}>
            <Trans>Cancel</Trans>
          </AlertDialogCancel>
          <Button
            variant={
              action?.kind === "grant" || action?.kind === "unblock" ? "default" : "destructive"
            }
            disabled={mutation.isPending || action === null}
            onClick={() => {
              if (!action) return;
              setError(null);
              mutation.mutate(action);
            }}
          >
            {mutation.isPending ? <Spinner aria-label={t`Working…`} /> : null}
            {action?.kind === "grant" ? <Trans>Make IT</Trans> : null}
            {action?.kind === "revoke" ? <Trans>Remove IT</Trans> : null}
            {action?.kind === "block" ? <Trans>Block</Trans> : null}
            {action?.kind === "unblock" ? <Trans>Unblock</Trans> : null}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function RowActions({
  user,
  isSelf,
  onAction,
}: {
  user: UserRow;
  isSelf: boolean;
  onAction: (a: Action) => void;
}) {
  return (
    <div className="flex flex-wrap justify-end gap-1">
      {user.isIt ? (
        <Button
          size="sm"
          variant="outline"
          disabled={isSelf}
          onClick={() => onAction({ kind: "revoke", user })}
        >
          <Trans>Remove IT</Trans>
        </Button>
      ) : (
        <Button size="sm" variant="outline" onClick={() => onAction({ kind: "grant", user })}>
          <Trans>Make IT</Trans>
        </Button>
      )}
      {user.blocked ? (
        <Button size="sm" variant="outline" onClick={() => onAction({ kind: "unblock", user })}>
          <Trans>Unblock</Trans>
        </Button>
      ) : (
        <Button
          size="sm"
          variant="outline"
          disabled={isSelf}
          onClick={() => onAction({ kind: "block", user })}
        >
          <Trans>Block</Trans>
        </Button>
      )}
    </div>
  );
}

/** Everyone in the deployment, with grant/revoke IT and block/unblock. */
export function AdminUsersPage() {
  const { t } = useLingui();
  const selfId = useQueryUserId();
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [action, setAction] = useState<Action | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => setSearch(input.trim()), 300);
    return () => clearTimeout(timer);
  }, [input]);
  const query = useInfiniteQuery({
    queryKey: usersKey(selfId, search),
    queryFn: ({ pageParam }) =>
      drokRpc.users.list({ search: search || undefined, cursor: pageParam, limit: 50 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: selfId !== null,
  });
  const users = query.data?.pages.flatMap((page) => page.users) ?? [];
  return (
    <section data-testid="admin-users">
      <Input
        type="search"
        aria-label={t`Search users`}
        placeholder={t`Search by name or email`}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        className="mb-4 max-w-sm"
      />
      {query.isPending ? (
        <div className="grid place-items-center p-8">
          <Spinner aria-label={t`Loading…`} />
        </div>
      ) : query.isError ? (
        <div role="alert" className="rounded-xl bg-muted/50 p-4 text-sm">
          <p className="text-destructive">{errorText(query.error)}</p>
          <Button className="mt-2" size="sm" variant="outline" onClick={() => void query.refetch()}>
            <Trans>Try again</Trans>
          </Button>
        </div>
      ) : users.length === 0 ? (
        <p className="rounded-xl bg-muted/50 p-4 text-sm text-muted-foreground">
          <Trans>No users found</Trans>
        </p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Trans>User</Trans>
                </TableHead>
                <TableHead>
                  <Trans>Status</Trans>
                </TableHead>
                <TableHead className="text-end">
                  <Trans>Actions</Trans>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} data-testid="admin-user-row">
                  <TableCell>
                    <p className="font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </TableCell>
                  <TableCell>
                    <StatusBadges user={user} />
                  </TableCell>
                  <TableCell>
                    <RowActions user={user} isSelf={user.id === selfId} onAction={setAction} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {query.hasNextPage ? (
            <Button
              className="mt-3"
              variant="outline"
              disabled={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              <Trans>Load more</Trans>
            </Button>
          ) : null}
        </div>
      )}
      <ActionDialog action={action} selfId={selfId} onClose={() => setAction(null)} />
    </section>
  );
}
